/**
 * Spec 014 — the history of a task's description, title and due date.
 *
 * A nested screen: back is Telegram's own BackButton. Every revision says who
 * changed the field and when, and shows the difference from the one before it.
 * «Стереть текст» is for a secret pasted by mistake. It asks first and says
 * what stays (number, author, time) and what cannot be undone: a Telegram
 * message that already went out. The current text is edited in the task
 * itself, so its revision offers no erase button.
 */

import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Tasks } from "@/api/resources";
import type { RevisionField, TaskRevisionOut } from "@/api/types";
import { DiffRow } from "@/components/TaskHistory";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { EmptyState, ErrorState } from "@/components/ui/EmptyState";
import { ScreenTitle } from "@/components/ui/Section";
import { Segmented } from "@/components/ui/Segmented";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { formatDateTime, parseDate } from "@/lib/format";
import { useBackButton } from "@/lib/telegram-ui";

const FIELDS = [
  { value: "description", label: "Описание" },
  { value: "title", label: "Название" },
  { value: "due", label: "Срок" },
] as const;

function when(value: string): string {
  const at = parseDate(value);
  return at ? formatDateTime(at) : "";
}

export function revisionTitle(r: TaskRevisionOut): string {
  if (r.origin === "before_history") return "Исходный текст — до ведения истории";
  if (r.number === 1) return "Исходный текст";
  return `Правка №${r.number}`;
}

export function revisionByline(r: TaskRevisionOut): string {
  if (r.origin === "before_history") return `Автор не записан · не позже ${when(r.created_at)}`;
  return `${r.author_name ?? "Участник"} · ${when(r.created_at)}`;
}

export function RevisionCard({
  revision,
  onErase,
  erasing,
}: {
  revision: TaskRevisionOut;
  onErase?: () => void;
  erasing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const r = revision;
  return (
    <Card className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-cell font-medium text-text">
          {revisionTitle(r)}
          {r.current && <span className="ml-2 text-meta text-hint">· сейчас</span>}
        </span>
      </div>
      <p className="text-meta text-hint">{revisionByline(r)}</p>
      {r.redacted ? (
        <p className="text-meta text-dstr">
          Текст удалён · {r.redacted.by_name} · {when(r.redacted.at)}
        </p>
      ) : (
        <>
          {r.diff.length > 0 && (
            <div className="space-y-0.5">
              {r.diff.map((line, i) => (
                <DiffRow key={i} line={line} />
              ))}
            </div>
          )}
          {r.diff_note && <p className="text-meta text-hint">{r.diff_note}</p>}
          {r.value != null && r.value !== "" && (
            <>
              <Button size="compact" variant="ghost" onClick={() => setOpen(!open)}>
                {open ? "Скрыть текст" : "Показать текст"}
              </Button>
              {open && (
                <p className="whitespace-pre-wrap break-words rounded-card bg-sbg p-3 text-body">
                  {r.value}
                </p>
              )}
            </>
          )}
          {onErase && (
            <Button size="compact" variant="ghost" loading={erasing} onClick={onErase}>
              Стереть текст
            </Button>
          )}
        </>
      )}
    </Card>
  );
}

export function TaskHistoryView() {
  const { task_id } = useParams<{ task_id: string }>();
  const [params, setParams] = useSearchParams();
  const field = (FIELDS.find((f) => f.value === params.get("field"))?.value ??
    "description") as RevisionField;
  const qc = useQueryClient();
  const toast = useToast();
  useBackButton(`/tasks/${task_id ?? ""}`);

  const history = useQuery({
    queryKey: ["task-history", task_id, field, "text"],
    queryFn: () => Tasks.history(task_id!, { field, with_text: true }),
    enabled: Boolean(task_id),
  });
  const erase = useMutation({
    mutationFn: (revisionId: string) => Tasks.redact(task_id!, revisionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["task-history", task_id] });
      void qc.invalidateQueries({ queryKey: ["chat"] });
      toast.success("Текст правки стёрт");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Не удалось стереть"),
  });

  const ask = (r: TaskRevisionOut) => {
    if (
      r.id &&
      confirm(
        `Стереть текст правки №${r.number}? Он исчезнет из истории, из обсуждения и из ещё ` +
          "не отправленных уведомлений. Номер, автор и время останутся. Уже отправленные " +
          "сообщения в Telegram не отзываются.",
      )
    ) {
      erase.mutate(r.id);
    }
  };

  if (!task_id) return null;
  const revisions = history.data?.revisions ?? [];
  return (
    <div className="space-y-4">
      <ScreenTitle eyebrow="Задача">История правок</ScreenTitle>
      <Segmented
        options={FIELDS}
        value={field}
        onChange={(value) => setParams({ field: value }, { replace: true })}
      />
      <p className="text-meta text-hint">
        Стереть можно любую правку, кроме текущей: если в тексте оказался секрет, сначала уберите
        его из задачи, затем сотрите прежнюю правку.
      </p>
      {history.isLoading && <SkeletonList count={3} />}
      {history.isError && (
        <ErrorState title="Не удалось загрузить историю" onRetry={() => void history.refetch()} />
      )}
      {!history.isLoading && !history.isError && revisions.length === 0 && (
        <EmptyState title="Правок пока нет" />
      )}
      {revisions.map((r) => (
        <RevisionCard
          key={r.id ?? `${r.field}-${r.number}`}
          revision={r}
          erasing={erase.isPending && erase.variables === r.id}
          onErase={r.id && !r.current && !r.redacted ? () => ask(r) : undefined}
        />
      ))}
    </div>
  );
}
